import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toolDetails, toolReviews, similarTools } from "@/data/toolDetails";

export default function ToolDetail() {
  const { id } = useParams<{ id: string }>();
  const toolId = id ? parseInt(id) : 0;
  const tool = toolDetails[toolId];
  const review = toolReviews[toolId];
  const similar = similarTools[toolId] || [];
  
  const [isFavorite, setIsFavorite] = useState(false);

  if (!tool) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Tool not found</h1>
            <Link to="/" className="text-[#1E90FF] hover:underline">
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? "Removed from favorites" : "Added to favorites");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          {/* Tool Basic Info */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img
                  src={tool.logoUrl}
                  alt={`${tool.name} logo`}
                  className="w-32 h-32 rounded-lg object-contain"
                />
              </div>
              <div className="flex-grow">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-3xl font-bold mb-2">{tool.name}</h1>
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`fa-solid fa-star ${
                            i < Math.floor(tool.rating)
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                        ></i>
                      ))}
                      <span className="text-gray-600 ml-2">
                        {tool.rating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={toggleFavorite}
                    className={`p-2 rounded-full ${
                      isFavorite
                        ? "text-red-500 bg-red-50"
                        : "text-gray-400 bg-gray-50"
                    } hover:bg-gray-100 transition-colors`}
                  >
                    <i
                      className={`fa-${
                        isFavorite ? "solid" : "regular"
                      } fa-heart text-xl`}
                    ></i>
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-700 mb-4">{tool.description}</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={tool.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-[#1E90FF] hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition-colors"
                    >
                      <i className="fa-solid fa-external-link mr-2"></i>
                      Visit Website
                    </a>
                    {tool.affiliateUrl && (
                      <a
                        href={tool.affiliateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-full transition-colors"
                      >
                        <i className="fa-solid fa-star mr-2"></i>
                        Get Deal
                      </a>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold mb-3">Key Features</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {tool.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <i className="fa-solid fa-check text-green-500 mt-1 mr-2"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Editor Review */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">Editor Review</h2>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">{review.review}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-700 mb-3">
                  Pros
                </h3>
                <ul className="space-y-2">
                  {review.pros.map((pro, index) => (
                    <li key={index} className="flex items-start">
                      <i className="fa-solid fa-check-circle text-green-500 mt-1 mr-2"></i>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-700 mb-3">Cons</h3>
                <ul className="space-y-2">
                  {review.cons.map((con, index) => (
                    <li key={index} className="flex items-start">
                      <i className="fa-solid fa-times-circle text-red-500 mt-1 mr-2"></i>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Similar Tools */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6">Similar Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {similar.map((item) => (
                <Link
                  key={item.id}
                  to={`/tools/${item.id}`}
                  className="block bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">{item.name}</h3>
                    <span className="text-sm text-gray-500">
                      {Math.round(item.similarity * 100)}% match
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
